
import { GoogleGenAI, Type } from "@google/genai";
import { Wine, WineType } from "../types";

const getApiKey = () => {
  // Nelle app statiche (Cloudflare, Vercel, ecc.), process.env.API_KEY viene sostituito 
  // dal valore reale durante il comando 'npm run build' sul tuo computer.
  const key = process.env.API_KEY;
  if (!key || key === "undefined" || key === "" || key.length < 10) {
    console.error("ERRORE CRITICO: API_KEY non trovata. Verifica il file .env e riavvia il build.");
    throw new Error("CONFIGURAZIONE_MANCANTE: API_KEY non disponibile.");
  }
  return key;
};

const sanitizeWineType = (type: string): WineType => {
  const t = (type || "").toLowerCase().trim();
  if (t.includes('ross')) return WineType.RED;
  if (t.includes('bianc')) return WineType.WHITE;
  if (t.includes('rosat')) return WineType.ROSE;
  if (t.includes('bollicin') || t.includes('spumant') || t.includes('champ')) return WineType.SPARKLING;
  if (t.includes('passit') || t.includes('dolce')) return WineType.DESSERT;
  if (t.includes('grappa') || t.includes('gin') || t.includes('liquor') || t.includes('distillat') || t.includes('amaro')) return WineType.SPIRITS;
  return WineType.OTHER;
};

export const extractWineInfoFromImages = async (base64Images: string[]): Promise<{ wine: Partial<Wine> }> => {
  const apiKey = getApiKey();
  const ai = new GoogleGenAI({ apiKey });
  
  const imageParts = base64Images.map(img => ({
    inlineData: { 
      mimeType: "image/jpeg", 
      data: img.includes(",") ? img.split(",")[1] : img 
    }
  }));

  const infoPrompt = `
    Analizza queste foto di una bottiglia. 
    Estrai JSON: name, producer, vintage, type (Rosso, Bianco, Rosato, Bollicina, Dolce, Liquore/Distillato), price (stima EUR), description, foodPairings (3 items), grapes, drinkingWindow.
  `;

  const infoPromise = ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: { parts: [...imageParts, { text: infoPrompt }] },
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          name: { type: Type.STRING },
          producer: { type: Type.STRING },
          vintage: { type: Type.STRING },
          type: { type: Type.STRING },
          price: { type: Type.NUMBER },
          description: { type: Type.STRING },
          grapes: { type: Type.STRING },
          foodPairings: { type: Type.ARRAY, items: { type: Type.STRING } },
          drinkingWindow: { type: Type.STRING },
        },
        required: ["name", "producer", "vintage", "type"]
      }
    }
  });

  const imagePrompt = `
    Professional Studio Product Photography: Pure white background, high quality bottle silhouette.
  `;

  const imagePromise = ai.models.generateContent({
    model: 'gemini-2.5-flash-image',
    contents: { parts: [imageParts[0], { text: imagePrompt }] },
    config: { imageConfig: { aspectRatio: "3:4" } }
  });

  try {
    const [infoRes, imgRes] = await Promise.all([infoPromise, imagePromise]);
    const info = JSON.parse(infoRes.text || "{}");
    
    let cutoutUrl = base64Images[0]; 
    if (imgRes.candidates?.[0]?.content?.parts) {
      for (const part of imgRes.candidates[0].content.parts) {
        if (part.inlineData) {
          cutoutUrl = `data:image/png;base64,${part.inlineData.data}`;
          break;
        }
      }
    }

    return {
      wine: {
        ...info,
        type: sanitizeWineType(info.type),
        price: Number(info.price) || 0,
        quantity: 1,
        addedAt: Date.now(),
        imageUrls: [cutoutUrl, base64Images[0]],
        review: "",
        notes: ""
      }
    };
  } catch (error: any) {
    throw new Error("Errore durante l'analisi IA.");
  }
};

export const getSommelierAdvice = async (cellar: Wine[], mealDescription: string): Promise<string> => {
  const apiKey = getApiKey();
  const ai = new GoogleGenAI({ apiKey });
  const wineList = cellar.map(w => `- ${w.name} (${w.producer}, ${w.vintage})`).join("\n");
  
  const prompt = `
    Sei un Sommelier d'Elite, conciso e tecnico. 
    Pasto: "${mealDescription}"
    Cantina:
    ${wineList}

    REGOLE MANDATORIE:
    1. Proponi SOLO 2 opzioni.
    2. NON scrivere introduzioni (es. "Ecco i miei consigli"). Vai subito al punto.
    3. Usa esattamente questo formato per ogni opzione, separandole con una riga vuota:

    [OPZIONE 1]
    🍷 **NOME VINO**
    💡 Perché: [Max 1 frase]
    🌡️ Servizio: [Temp. | Apertura: XX min | Decanter: Sì/No | Calice: Tipo]

    [OPZIONE 2]
    🍷 **NOME VINO**
    💡 Perché: [Max 1 frase]
    🌡️ Servizio: [Temp. | Apertura: XX min | Decanter: Sì/No | Calice: Tipo]

    Sii telegrafico.
  `;
  
  const response = await ai.models.generateContent({ 
    model: "gemini-3-flash-preview", 
    contents: prompt 
  });
  return response.text || "Nessun abbinamento trovato.";
};
